import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { api, errMsg } from "../../lib/api";
import { refreshBranding } from "../../lib/branding";
import { toCanvas, readFile } from "../../lib/image";
import { PageHead } from "../../components/Bits";
import { ReportSectionDefaults } from "../../components/ReportSectionDefaults";

const ROOM_LABELS = {
  entrance: "Entrance",
  living: "Living Room",
  dining: "Dining Area",
  balcony: "Balcony",
  kitchen: "Kitchen",
  utility: "Utility",
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  passage: "Passage/Common Areas",
  db: "Electrical Distribution Board",
  plumbing_overall: "Overall Plumbing",
  doors_windows_overall: "Overall Doors & Windows",
  finishing_overall: "Overall Finishing",
  staircase: "Staircase (villa/house)",
  terrace: "Terrace (villa/house)",
  exterior: "External / Compound (villa/house)",
};

function Checklist({ value, onChange }) {
  const [rt, setRt] = useState("bedroom");
  const cats = value[rt] || {};
  return (
    <div className="grid gap-4 md:grid-cols-[220px_1fr]">
      <div className="space-y-1">
        {Object.keys(value).map((k) => (
          <button
            key={k}
            onClick={() => setRt(k)}
            className={`block w-full rounded-md px-3 py-2 text-left text-sm ${rt === k ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}
            data-testid={`checklist-room-${k}`}
          >
            {ROOM_LABELS[k] || k}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(cats).map(([c, items]) => (
          <div key={c} className="rounded-md border border-slate-200 p-3">
            <div className="mb-2 font-mono text-[10px] font-bold tracking-widest text-teal-700">
              {c.toUpperCase()}
            </div>
            <Textarea
              rows={Math.max(3, items.length)}
              value={items.join("\n")}
              data-testid={`checklist-items-${rt}-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onChange={(e) =>
                onChange({
                  ...value,
                  [rt]: { ...cats, [c]: e.target.value.split("\n") },
                })
              }
            />
          </div>
        ))}
        <p className="text-xs text-slate-500 sm:col-span-2">
          One item per line. Changes apply to new inspections; existing
          inspections keep their template.
        </p>
      </div>
    </div>
  );
}

function LogoUpload({
  title,
  description,
  src,
  onUpload,
  onRemove,
  testPrefix,
}) {
  return (
    <div
      className="rounded-lg border border-slate-200 p-4"
      data-testid={`${testPrefix}-section`}
    >
      <div className="font-display text-lg font-bold">{title}</div>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        {description}
      </p>
      <div className="mt-4 grid h-24 place-items-center rounded-md border border-dashed border-slate-300 bg-slate-50 p-2">
        {src ? (
          <img
            src={src}
            alt={`${title} preview`}
            className="max-h-full max-w-full object-contain"
            data-testid={`${testPrefix}-preview`}
          />
        ) : (
          <span className="text-xs text-slate-400">
            No logo — default wordmark
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <label
          className="tap cursor-pointer rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
          data-testid={`${testPrefix}-upload-button`}
        >
          Upload logo
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onUpload}
            data-testid={`${testPrefix}-input`}
          />
        </label>
        {src && (
          <button
            onClick={onRemove}
            className="tap rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold"
            data-testid={`${testPrefix}-remove-button`}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState([]);
  const [enq, setEnq] = useState([]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data));
    api.get("/admin/audit").then(({ data }) => setLogs(data));
    api.get("/admin/enquiries").then(({ data }) => setEnq(data));
  }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const checklist = Object.fromEntries(
        Object.entries(s.checklist).map(([k, cats]) => [
          k,
          Object.fromEntries(
            Object.entries(cats).map(([c, it]) => [
              c,
              it.map((x) => x.trim()).filter(Boolean),
            ]),
          ),
        ]),
      );
      const lines = (v) =>
        (Array.isArray(v) ? v : String(v || "").split("\n"))
          .map((x) => x.trim())
          .filter(Boolean);
      setS(
        await api
          .put("/settings", {
            ...s,
            checklist,
            limitations: lines(s.limitations),
            disclaimer: lines(s.disclaimer),
          })
          .then((r) => r.data),
      );
      refreshBranding();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const b = (k) => (e) =>
    setS((x) => ({ ...x, branding: { ...x.branding, [k]: e.target.value } }));
  const logoFile = (field) => async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const c = await toCanvas(await readFile(f), 480);
    setS((x) => ({
      ...x,
      branding: { ...x.branding, [field]: c.toDataURL("image/png") },
    }));
  };
  const removeLogo = (field) => () => {
    const otherField =
      field === "website_logo" ? "report_logo" : "website_logo";
    setS((x) => {
      const branding = { ...x.branding };
      if (branding.logo && !branding[otherField]) {
        branding[otherField] = branding.logo;
      }
      branding[field] = "";
      branding.logo = "";
      return { ...x, branding };
    });
  };
  const legal = (k) => (Array.isArray(s[k]) ? s[k].join("\n") : s[k] || "");
  return (
    <div>
      <PageHead over="Configuration" title="Settings & checklist">
        <button
          onClick={save}
          className="tap h-11 rounded-md bg-slate-900 px-6 text-sm font-semibold text-white"
          data-testid="settings-save-button"
        >
          Save changes
        </button>
      </PageHead>
      <Tabs defaultValue="branding">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 p-1 sm:grid-cols-4 lg:flex lg:h-10 lg:w-auto">
          {[
            "branding",
            "legal",
            "reports",
            "checklist",
            "severity",
            "pricing",
            "enquiries",
            "audit",
          ].map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              className="w-full px-2 text-xs capitalize sm:text-sm lg:w-auto"
              data-testid={`settings-tab-${t}`}
            >
              {t === "audit"
                ? "Audit log"
                : t === "legal"
                  ? "Disclaimer"
                  : t === "reports"
                    ? "Report defaults"
                    : t}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value="branding"
          className="mt-4 rounded-xl border border-slate-200 bg-white p-5"
        >
          <div
            className="mb-6 grid gap-4 md:grid-cols-2"
            data-testid="branding-logo-section"
          >
            <LogoUpload
              title="Website logo"
              description="Used in the public website and app headers."
              src={s.branding.website_logo || s.branding.logo}
              onUpload={logoFile("website_logo")}
              onRemove={removeLogo("website_logo")}
              testPrefix="branding-website-logo"
            />
            <LogoUpload
              title="Report logo"
              description="Used only on generated PDF inspection report covers."
              src={s.branding.report_logo || s.branding.logo}
              onUpload={logoFile("report_logo")}
              onRemove={removeLogo("report_logo")}
              testPrefix="branding-report-logo"
            />
            <p className="text-xs text-slate-500 md:col-span-2">
              PNG with a transparent background works best. Uploading a new logo
              does not alter reports already generated; regenerated PDFs use the
              report logo saved here.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["company", "Company name"],
              ["tagline", "Tagline"],
              ["website", "Website"],
              ["phone", "Phone"],
              [
                "whatsapp",
                "WhatsApp number (with country code, e.g. +91 98xxxxxxxx)",
              ],
              ["email", "Email"],
              ["address", "Address"],
            ].map(([k, l]) => (
              <div key={k}>
                <div className="mb-1 text-xs font-medium">{l}</div>
                <Input
                  className="h-11"
                  value={s.branding[k] || ""}
                  onChange={b(k)}
                  data-testid={`branding-${k}-input`}
                />
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Used on the PDF cover, contact page and report emails.
          </p>
        </TabsContent>
        <TabsContent
          value="legal"
          className="mt-4 space-y-5 rounded-xl border border-slate-200 bg-white p-5"
        >
          <div>
            <div className="overline mb-2">Inspection limitations</div>
            <Textarea
              rows={7}
              value={legal("limitations")}
              onChange={(e) =>
                setS((x) => ({ ...x, limitations: e.target.value }))
              }
              data-testid="legal-limitations-input"
            />
          </div>
          <div>
            <div className="overline mb-2">Disclaimer</div>
            <Textarea
              rows={8}
              value={legal("disclaimer")}
              onChange={(e) =>
                setS((x) => ({ ...x, disclaimer: e.target.value }))
              }
              data-testid="legal-disclaimer-input"
            />
          </div>
          <p className="text-xs text-slate-500">
            One point per line. Appears on the online report and the PDF's
            limitations & disclaimer pages for all reports, including existing
            ones.
          </p>
        </TabsContent>
        <TabsContent
          value="reports"
          className="mt-4 rounded-xl border border-slate-200 bg-white p-5"
        >
          <ReportSectionDefaults
            value={s.report_sections}
            onChange={(report_sections) =>
              setS((x) => ({ ...x, report_sections }))
            }
          />
        </TabsContent>
        <TabsContent
          value="checklist"
          className="mt-4 rounded-xl border border-slate-200 bg-white p-5"
        >
          <Checklist
            value={s.checklist}
            onChange={(v) => setS((x) => ({ ...x, checklist: v }))}
          />
        </TabsContent>
        <TabsContent
          value="severity"
          className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-white p-5"
        >
          {s.severities.map((v, i) => (
            <div
              key={v.key}
              className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center"
            >
              <span className="font-mono text-xs font-bold">{v.key}</span>
              <Input
                value={v.desc}
                onChange={(e) =>
                  setS((x) => ({
                    ...x,
                    severities: x.severities.map((y, j) =>
                      j === i ? { ...y, desc: e.target.value } : y,
                    ),
                  }))
                }
                data-testid={`severity-${v.key.toLowerCase()}-input`}
              />
            </div>
          ))}
          <p className="text-xs text-slate-500">
            The system never auto-classifies a crack as structural. Inspectors
            decide severity; AI only suggests.
          </p>
        </TabsContent>
        <TabsContent
          value="pricing"
          className="mt-4 rounded-xl border border-slate-200 bg-white p-5"
        >
          <p className="mb-4 text-xs text-amber-700">{s.pricing_note}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {Object.entries(s.pricing).map(([k, v]) => (
              <div key={k}>
                <div className="mb-1 text-xs font-medium">{k}</div>
                <Input
                  type="number"
                  className="h-11"
                  value={v}
                  onChange={(e) =>
                    setS((x) => ({
                      ...x,
                      pricing: { ...x.pricing, [k]: Number(e.target.value) },
                    }))
                  }
                  data-testid={`pricing-${k.toLowerCase().replace(/[^a-z]+/g, "-")}-input`}
                />
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent
          value="enquiries"
          className="mt-4 space-y-2"
          data-testid="enquiries-list"
        >
          {enq.map((q) => (
            <div
              key={q.enquiry_id}
              className="flex flex-wrap items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm"
              data-testid={`enquiry-${q.enquiry_id}`}
            >
              <div className="flex-1">
                <div className="font-semibold">
                  {q.name} ·{" "}
                  <a href={`tel:${q.phone}`} className="text-teal-700">
                    {q.phone}
                  </a>
                  {q.email && ` · ${q.email}`}
                </div>
                <p className="mt-1 text-slate-600">{q.message || "-"}</p>
                <div className="mt-1 font-mono text-[10px] text-slate-400">
                  {new Date(q.at).toLocaleString("en-IN")}
                </div>
              </div>
              <a
                href={`https://wa.me/${q.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-md bg-[#25D366] px-3 py-2 text-xs font-semibold text-white"
                data-testid={`enquiry-whatsapp-${q.enquiry_id}`}
              >
                WhatsApp
              </a>
              <button
                onClick={async () => {
                  await api.delete(`/admin/enquiries/${q.enquiry_id}`);
                  setEnq((x) => x.filter((y) => y.enquiry_id !== q.enquiry_id));
                }}
                className="rounded-md border border-slate-200 px-3 py-2 text-xs"
                data-testid={`enquiry-delete-${q.enquiry_id}`}
              >
                Delete
              </button>
            </div>
          ))}
          {!enq.length && (
            <p className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
              No enquiries yet.
            </p>
          )}
        </TabsContent>
        <TabsContent
          value="audit"
          className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white"
        >
          <table
            className="w-full min-w-[640px] text-sm"
            data-testid="audit-table"
          >
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left font-mono text-[10px] uppercase text-slate-500">
                <th className="px-4 py-2">Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Target</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.log_id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">
                    {new Date(l.at).toLocaleString("en-IN")}
                  </td>
                  <td>{l.user_name}</td>
                  <td className="font-mono text-xs">{l.action}</td>
                  <td className="font-mono text-xs">{l.target}</td>
                  <td className="text-xs text-slate-500">{l.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>
      </Tabs>
    </div>
  );
}
